import { useQuery, useMutation } from "@tanstack/react-query";
import { fetch3, fetch4, fetch5, fetch8 } from "../commonApis/commonApis";
const url = process.env.REACT_APP_BASE_URL;

interface ApiResponse {
  success: boolean;
  data: any;
  message?: string;
  statusCode?: number;
  statusMessage?: string;
  error?: any;
}

interface StaffPayload {
  id?: string;
  email: string;
  fullName: string;
  city: string;
  country: string;
  phone: string;
  state: string;
  address?: string;
  iqamaNo?: string;
  staffRolesId?: string;
  type?: string;
}

export const useStaffsData = (page: number, limit: number, searchFor: string) => {
  return useQuery({
    queryKey: ["staffsData"],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/outlets/outletstaff?page=${page}&limit=${limit}&searchFor=${searchFor}&type=outlet`,
        "get",
      );

      if (!res || !res.data) {
        return res.message;
      }
      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useFetchOutletSingleStaff = (id?: string) => {
  return useQuery({
    queryKey: ["outletSingleStaff", id],
    queryFn: async () => {
      if (!id) return null;
      const res = await fetch3(
        `${url}/api/outlets/outletstaff/${id}?type=outlet`,
        "get",
      );
      if (!res || !res.data) return res.message;
      return res.data;
    },
    enabled: !!id,
  });
};

export const useFetchStaffsAllData = () => {
  return useQuery({
    queryKey: ["staffsAllData"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/staff/all?type=LM`, "get");

      if (!res || !res.data) {
        return res.message;
      }

      return res.data; // 👈 This returns your full user object
    },
    enabled: true,
    staleTime: 1000 * 60 * 5,
  });
};

export const useStaffDetailsData = (id?: string) => {
  return useQuery({
    queryKey: ["staffDetailsData", id],
    queryFn: async () => {
      if (!id) return null; // 👈 early exit inside the hook logic
      const res = await fetch3(`${url}/api/staff/${id}?type=LM`, "get");
      if (!res || !res.data) return res.message;
      return res.data;
    },
    enabled: !!id, // 👈 run only when id exists
    staleTime: 1000 * 60 * 5,
  });
};

export const useStaffDocsData = (id?: string) => {
  return useQuery({
    queryKey: ["staffDocsData", id],
    queryFn: async () => {
      if (!id) return null; // 👈 early exit inside the hook logic
      const res = await fetch3(`${url}/api/staff/${id}/docs?type=LM`, "get");
      if (!res || !res.data) return res.message;
      return res.data;
    },
    enabled: !!id, // 👈 run only when id exists
    staleTime: 1000 * 60 * 5,
  });
};

export const useStaffCreateMutation = () => {
  return useMutation({
    mutationFn: async (payload: StaffPayload) => {
      const formData = new FormData();

      Object.entries(payload).forEach(([key, value]) => {
        if (key === "warehouses" && Array.isArray(value)) {
          value.forEach((warehouse) =>
            formData.append("warehouses", warehouse),
          );
        } else if (value instanceof File) {
          formData.append(key, value);
        } else {
          formData.append(key, value as string);
        }
      });

      const res = await fetch(`${url}/api/staff`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      const data = await res.json();

      if (!data.success) {
        throw new Error(data.message || "Something went wrong");
      }

      return data.data;
    },

    onSuccess: (data: any) => {
      console.log("✅ Staff Created successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Staff create request failed:", error.message);
    },
  });
};

export const useStaffUpdateMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/staff/${payload?.id}`,
        "PATCH",
        payload,
      );

      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return { ...res.data, message: res.message };
    },
    onSuccess: (data) => {
      console.log("✅ Staff updated successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Staff update request failed:", error.message);
    },
  });
};

export const useStaffRoleCreateMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch4(`${url}/api/roles`, payload);

      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Staff role Created successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ staff role create request failed:", error.message);
    },
  });
};

export const useStaffRoleUpdateMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/roles/${payload?.id}`,
        "PATCH",
        payload,
      );
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Staff role updated successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ staff role update request failed:", error.message);
    },
  });
};

export const useRolesData = () => {
  return useQuery({
    queryKey: ["rolesData"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/roles?type=LM`, "get");

      if (!res || !res.data) {
        return res.message;
      }

      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useFetchStaffTemplate = () => {
  return useQuery({
    queryKey: ["templates"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/templates/staffs`, "get");
      if (!res || !res.data) return res.message;
      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useStaffBulkDocMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch8(
        `${url}/api/bulk/bulk-staff`,
        "POST",
        payload,
      );
 
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res;
    },
    onSuccess: (res: any) => {
      console.log("Staff Created successfully:", res);
      return res;
    },
    onError: (error: any) => {
      console.error("Staff create request failed:", error.message);
    },
  });
};
